import React, { useState, useEffect } from "react";
import { getAllBlogs } from "../services/api";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FaSearch, FaArrowRight, FaChevronLeft, FaChevronRight } from "react-icons/fa";

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } };
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] } } };

const categoryColors = {
  "System Design": "text-violet-400 bg-violet-500/10 border-violet-500/20",
  "DSA": "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
  "Frontend": "text-cyan-400 bg-cyan-500/10 border-cyan-500/20",
  "Backend": "text-orange-400 bg-orange-500/10 border-orange-500/20",
};

const getReadingTime = (content) => {
  const words = content?.split(/\s+/).length || 0;
  return Math.max(1, Math.ceil(words / 200));
};

const BlogPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const fetchBlogs = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getAllBlogs(page, category, debouncedSearch);
        setBlogs(data.blogs);
        setTotalPages(data.totalPages);
      } catch (err) {
        setError("Error fetching articles. Please try again.");
      }
      setLoading(false);
    };
    fetchBlogs();
  }, [page, category, debouncedSearch]);

  const categories = ["System Design", "DSA", "Frontend", "Backend"];

  return (
    <div className="relative text-white max-w-5xl mx-auto px-6 py-16 overflow-hidden min-h-[80vh]">
      <div className="noise-overlay"></div>

      {/* Ambient glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[400px] h-[400px] bg-violet-600/6 rounded-full blur-[120px] animate-drift"></div>
        <div className="absolute bottom-0 -left-40 w-[400px] h-[400px] bg-blue-600/5 rounded-full blur-[120px] animate-drift-reverse"></div>
      </div>

      <div className="relative z-10">
        {/* Header */}
        <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <p className="text-[10px] text-gray-600 uppercase tracking-[0.4em] font-mono mb-2">05 / Articles</p>
          <h2 className="text-4xl md:text-5xl font-black tracking-tight">Thoughts &<br /><span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-400 to-pink-400">deep dives</span></h2>
        </motion.div>

        {/* Search + filters */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2, duration: 0.5 }}
          className="mt-10 flex flex-col sm:flex-row gap-4 items-start sm:items-center"
        >
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-700 text-xs" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search articles..."
              className="w-full pl-9 pr-4 py-2.5 bg-white/[0.02] text-white text-sm border border-gray-800/60 rounded-lg focus:border-violet-500/40 focus:bg-white/[0.04] outline-none transition-all duration-300 placeholder-gray-700 font-light"
            />
          </div>

          {/* Category pills */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => { setCategory(""); setPage(1); }}
              className={`px-3 py-1.5 text-xs font-mono rounded-full border transition-all duration-300 ${
                category === "" ? "text-white bg-white/10 border-white/20" : "text-gray-600 border-gray-800/50 hover:text-gray-400"
              }`}
            >all</button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => { setCategory(cat); setPage(1); }}
                className={`px-3 py-1.5 text-xs font-mono rounded-full border transition-all duration-300 ${
                  category === cat
                    ? (categoryColors[cat] || "text-white bg-white/10 border-white/20")
                    : "text-gray-600 border-gray-800/50 hover:text-gray-400"
                }`}
              >{cat.toLowerCase()}</button>
            ))}
          </div>
        </motion.div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center items-center h-60">
            <div className="w-6 h-6 border-2 border-violet-400/40 border-t-violet-400 rounded-full animate-spin"></div>
          </div>
        ) : error ? (
          <p className="text-center text-red-400 mt-20 text-sm">{error}</p>
        ) : blogs.length === 0 ? (
          <div className="text-center mt-20">
            <p className="text-gray-600 text-sm">No articles found</p>
            <p className="text-gray-800 text-xs font-mono mt-1">try a different search or category</p>
          </div>
        ) : (
          <>
            {/* Article list — editorial style */}
            <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} className="mt-12 space-y-0">
              {blogs.map((blog, i) => (
                <motion.div key={blog._id} variants={fadeUp}>
                  <Link to={`/blog/${blog._id}`} className="group block">
                    <div className="grid grid-cols-12 gap-4 py-8 border-t border-gray-800/60 hover:border-gray-600/60 transition-all duration-300 hover:bg-white/[0.01] rounded-lg">
                      {/* Number */}
                      <div className="col-span-1 hidden md:block">
                        <span className="text-3xl font-black text-gray-800/60 group-hover:text-violet-500/30 transition-colors duration-500 select-none tabular-nums">
                          {String(i + 1 + (page - 1) * 10).padStart(2, "0")}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="col-span-12 md:col-span-7">
                        {/* Category + reading time */}
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-2 py-0.5 text-[10px] font-mono rounded-full border ${categoryColors[blog.category] || "text-gray-400 bg-gray-800/40 border-gray-700/50"}`}>
                            {blog.category}
                          </span>
                          <span className="text-[10px] text-gray-700 font-mono">{getReadingTime(blog.content)} min read</span>
                        </div>

                        {/* Title */}
                        <h3 className="text-lg md:text-xl font-bold text-white group-hover:text-violet-300 transition-colors duration-300 leading-tight mb-2">
                          {blog.title}
                        </h3>

                        {/* Excerpt */}
                        <p className="text-sm text-gray-500 leading-relaxed line-clamp-2 font-light">
                          {blog.content.replace(/[#*`>\-\[\]()]/g, "").substring(0, 180)}...
                        </p>

                        {/* Date + arrow */}
                        <div className="flex items-center justify-between mt-4">
                          <span className="text-[10px] text-gray-700 font-mono">
                            {new Date(blog.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-gray-700 group-hover:text-violet-400 transition-colors duration-300">
                            Read <FaArrowRight className="text-[8px] group-hover:translate-x-1 transition-transform duration-300" />
                          </span>
                        </div>
                      </div>

                      {/* Thumbnail */}
                      <div className="col-span-12 md:col-span-4 order-first md:order-last">
                        {blog.images?.length > 0 ? (
                          <div className="relative overflow-hidden rounded-lg aspect-[16/10] border border-gray-800/50 group-hover:border-gray-700/60 transition-colors duration-500">
                            <img src={blog.images[0]} alt={blog.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                          </div>
                        ) : (
                          <div className="rounded-lg aspect-[16/10] bg-gradient-to-br from-violet-500/5 to-pink-500/5 border border-gray-800/50 flex items-center justify-center">
                            <span className="text-3xl font-black text-gray-800/40 font-mono select-none">{blog.title?.charAt(0)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>

            {/* Pagination — minimal */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-12 pt-8 border-t border-gray-800/40">
                <button onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={page === 1}
                  className="p-2 text-gray-600 hover:text-white disabled:opacity-20 transition-colors">
                  <FaChevronLeft className="text-xs" />
                </button>
                <div className="flex gap-1">
                  {[...Array(totalPages)].map((_, i) => (
                    <button key={i} onClick={() => setPage(i + 1)}
                      className={`w-8 h-8 rounded-lg text-xs font-mono transition-all duration-300 ${
                        page === i + 1 ? "bg-violet-500/20 text-violet-400 border border-violet-500/30" : "text-gray-700 hover:text-gray-400"
                      }`}
                    >{i + 1}</button>
                  ))}
                </div>
                <button onClick={() => setPage((p) => Math.min(p + 1, totalPages))} disabled={page === totalPages}
                  className="p-2 text-gray-600 hover:text-white disabled:opacity-20 transition-colors">
                  <FaChevronRight className="text-xs" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
